import { useEffect, useRef, useState } from 'react'
import { IconoBuscar } from '../../componentes/iconos'
import type {
  DiaRuta,
  FiltrosAgendamiento,
  SemanaReparto,
} from '../../hooks/useFiltrosAgendamiento'
import { useSugerenciasClientes } from './consultas'
import { DIAS, SEMANAS } from './tabla'

/**
 * Los filtros de la tabla: el dia de ruta, la semana y el buscador.
 *
 * El dia esta **a la vista y a un clic**, no dentro de un desplegable, porque es
 * el filtro que se usa todos los dias: la pregunta con la que se abre esta
 * pantalla es "quien reparte hoy". Un desplegable son dos gestos y esconde cual
 * esta puesto.
 *
 * El buscador es un autocompletado: al escribir sugiere clientes por **nombre y
 * por NIF** -muchos se buscan por NIF porque el nombre esta escrito de tres
 * formas distintas-, y elegir uno deja su NIF como filtro. Escribir sin elegir
 * tambien vale: se aplica el texto tal cual como busqueda.
 */

/** Sin espera, cada tecla seria una peticion a un endpoint que tarda segundos. */
const ESPERA_BUSQUEDA = 300

/** El backend exige dos caracteres; con menos no se sugiere nada. */
const MINIMO_BUSQUEDA = 2

const CLASE_CHIP =
  'rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foco'
const CHIP_ACTIVO = 'border-acento-borde bg-acento-suave text-acento-tinta'
const CHIP_INACTIVO = 'border-borde bg-superficie text-tinta-2 hover:bg-elevado'

interface Props {
  filtros: FiltrosAgendamiento
  onRuta: (ruta: DiaRuta | '') => void
  onSemana: (semana: SemanaReparto | '') => void
  onBuscar: (q: string) => void
}

function Chip({
  activo,
  etiqueta,
  onPulsar,
}: {
  activo: boolean
  etiqueta: string
  onPulsar: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={activo}
      onClick={onPulsar}
      className={`${CLASE_CHIP} ${activo ? CHIP_ACTIVO : CHIP_INACTIVO}`}
    >
      {etiqueta}
    </button>
  )
}

export function FiltrosTabla({ filtros, onRuta, onSemana, onBuscar }: Props) {
  const [texto, setTexto] = useState(filtros.q)
  // El texto ya reposado. Es el que se le pregunta al backend; `texto` es lo que
  // se esta tecleando y cambia con cada letra.
  const [reposado, setReposado] = useState(filtros.q)
  const [abierto, setAbierto] = useState(false)
  // Ultimo valor que este componente ha puesto en la url. Sirve para distinguir
  // "lo he escrito yo" de "la url ha cambiado por fuera" (atras del navegador).
  const ultimoEnviado = useRef(filtros.q)

  useEffect(() => {
    if (filtros.q === ultimoEnviado.current) return
    ultimoEnviado.current = filtros.q
    setTexto(filtros.q)
    setReposado(filtros.q)
    setAbierto(false)
  }, [filtros.q])

  useEffect(() => {
    const temporizador = setTimeout(() => setReposado(texto), ESPERA_BUSQUEDA)
    return () => clearTimeout(temporizador)
  }, [texto])

  const sugerencias = useSugerenciasClientes(reposado, filtros.ruta, filtros.semana, abierto)
  const encontrados = sugerencias.data?.items ?? []
  const desplegado = abierto && reposado.trim().length >= MINIMO_BUSQUEDA

  const aplicar = (valor: string) => {
    ultimoEnviado.current = valor
    setTexto(valor)
    setReposado(valor)
    setAbierto(false)
    onBuscar(valor)
  }

  return (
    <div className="flex flex-col gap-3">
      <div
        role="group"
        aria-label="Dia de ruta"
        className="flex flex-wrap items-center gap-2"
      >
        <span className="text-sm font-medium text-tinta-2">Dia de ruta</span>
        <Chip activo={filtros.ruta === ''} etiqueta="Todos" onPulsar={() => onRuta('')} />
        {DIAS.map((dia) => (
          <Chip
            key={dia.clave}
            activo={filtros.ruta === dia.clave}
            etiqueta={dia.etiqueta}
            onPulsar={() => onRuta(dia.clave)}
          />
        ))}
      </div>

      <div role="group" aria-label="Semana" className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-tinta-2">Semana</span>
        <Chip activo={filtros.semana === ''} etiqueta="Todas" onPulsar={() => onSemana('')} />
        {SEMANAS.map((semana) => (
          <Chip
            key={semana.clave}
            activo={filtros.semana === semana.clave}
            etiqueta={semana.etiqueta}
            onPulsar={() => onSemana(semana.clave)}
          />
        ))}
      </div>

      <div className="relative max-w-lg">
        <label htmlFor="buscador-tabla" className="block text-sm font-medium text-tinta-2">
          Buscar cliente
        </label>
        <form
          role="search"
          onSubmit={(evento) => {
            evento.preventDefault()
            aplicar(texto)
          }}
        >
          <div className="relative mt-1">
            <IconoBuscar
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-tinta-apagada"
            />
            <input
              id="buscador-tabla"
              type="search"
              autoComplete="off"
              placeholder="Nombre o NIF"
              value={texto}
              onChange={(evento) => {
                setTexto(evento.target.value)
                setAbierto(true)
              }}
              onKeyDown={(evento) => {
                if (evento.key === 'Escape') setAbierto(false)
              }}
              className="w-full rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 pl-9 text-sm text-tinta outline-none focus:border-foco focus:ring-2 focus:ring-foco-halo"
            />
          </div>
        </form>

        {desplegado ? (
          // `isPending` ademas de `isFetching`: entre que el texto reposa y la
          // peticion arranca hay un hueco de un render, y una lista vacia
          // parpadeando en el diria "ningun cliente coincide" sin haber
          // preguntado todavia.
          (sugerencias.isPending || sugerencias.isFetching) && encontrados.length === 0 ? (
            <p
              role="status"
              aria-live="polite"
              className="absolute z-10 mt-1 w-full rounded-lg border border-borde bg-superficie px-3 py-2 text-sm text-tinta-apagada shadow-lg"
            >
              Buscando clientes
            </p>
          ) : (
            <ul
              aria-label="Sugerencias de clientes"
              className="absolute z-10 mt-1 flex w-full flex-col overflow-hidden rounded-lg border border-borde bg-superficie shadow-lg"
            >
              {encontrados.map((fila) => (
                <li key={fila.cliente.contact_id}>
                  <button
                    type="button"
                    // El NIF y no el nombre: es lo que identifica al cliente en
                    // el albaran, y dos clientes pueden llamarse igual.
                    onClick={() => aplicar(fila.cliente.code || fila.cliente.nombre)}
                    className="flex w-full flex-wrap items-baseline gap-2 px-3 py-2 text-left transition-colors hover:bg-elevado"
                  >
                    <span className="text-sm font-medium text-tinta">{fila.cliente.nombre}</span>
                    <span className="text-xs text-tinta-apagada">{fila.cliente.code}</span>
                    <span className="text-xs text-tinta-apagada">{fila.cliente.ciudad}</span>
                  </button>
                </li>
              ))}
              {encontrados.length === 0 ? (
                <li className="px-3 py-2 text-sm text-tinta-apagada">Ningun cliente coincide</li>
              ) : null}
              <li>
                <button
                  type="button"
                  onClick={() => aplicar(texto)}
                  className="w-full border-t border-borde-suave px-3 py-2 text-left text-sm text-tinta-2 transition-colors hover:bg-elevado"
                >
                  Buscar {texto} en toda la tabla
                </button>
              </li>
            </ul>
          )
        ) : null}
      </div>
    </div>
  )
}
